import React, { useState } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useBrandById, useModelById } from '../store-hybrid';

export const NotificationBell: React.FC = () => {
  const { rentalNotifications, stats } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);

  if (stats.total === 0) {
    return null;
  }

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-all duration-200 ${
          stats.hasUrgent 
            ? 'text-red-600 bg-red-50 hover:bg-red-100 animate-pulse' 
            : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
        }`}
      >
        {/* Bell Icon */}
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {/* Notification Badge */}
        <div className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-white rounded-full ${
          stats.hasUrgent ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
        }`}>
          {stats.total > 99 ? '99+' : stats.total}
        </div>
        
        {/* Urgent indicator */}
        {stats.hasUrgent && (
          <div className="absolute -top-2 -right-2 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-20 max-h-96 overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">🚨 Rental Alerts</h3>
                <div className="flex items-center space-x-2 text-sm">
                  {stats.critical > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                      {stats.critical} Critical
                    </span>
                  )}
                  <span className="text-gray-500">{stats.total} total</span>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center space-x-4 mt-2 text-xs">
                {stats.overdue > 0 && (
                  <span className="text-red-600 font-medium">🚨 {stats.overdue} Overdue</span>
                )}
                {stats.dueVerySoon > 0 && (
                  <span className="text-red-500 font-medium">⚠️ {stats.dueVerySoon} Due Soon</span>
                )}
                {stats.dueSoon > 0 && (
                  <span className="text-orange-500 font-medium">⏰ {stats.dueSoon} Due Today</span>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div className="divide-y divide-gray-100">
              {rentalNotifications.map((notification) => (
                <NotificationItem 
                  key={notification.id} 
                  notification={notification}
                  onClose={() => setIsOpen(false)}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="p-3 bg-gray-50 text-center">
              <button
                onClick={() => setIsOpen(false)}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close Notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Individual Notification Item Component
const NotificationItem: React.FC<{ 
  notification: any; 
  onClose: () => void 
}> = ({ notification, onClose }) => {
  const brand = useBrandById(notification.car.brandId);
  const model = useModelById(notification.car.modelId);

  const getNotificationStyle = () => {
    switch (notification.type) {
      case 'overdue':
        return 'bg-red-50 border-l-4 border-red-500';
      case 'due-very-soon':
        return 'bg-red-25 border-l-4 border-red-400';
      case 'due-soon':
        return 'bg-orange-25 border-l-4 border-orange-400';
      case 'due-today':
        return 'bg-yellow-25 border-l-4 border-yellow-400';
      default:
        return 'bg-gray-50 border-l-4 border-gray-400';
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'overdue':
        return '🚨';
      case 'due-very-soon':
        return '⚠️';
      case 'due-soon':
        return '⏰';
      case 'due-today':
        return '📅';
      default:
        return '🔔';
    }
  };

  return (
    <div className={`p-4 hover:bg-gray-25 transition-colors cursor-pointer ${getNotificationStyle()}`}>
      <div className="flex items-start space-x-3">
        <div className="text-lg">{getIcon()}</div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-900 truncate">
              {brand?.name} {model?.name}
            </h4>
            <span className={`px-2 py-1 text-xs font-bold rounded-full ${
              notification.urgency === 'critical' 
                ? 'bg-red-100 text-red-800'
                : notification.urgency === 'high'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-yellow-100 text-yellow-800'
            }`}>
              {notification.urgency.toUpperCase()}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 mt-1">
            {notification.car.plate} • {notification.message}
          </p>
          
          <div className="flex items-center justify-between mt-2">
            <span className={`text-sm font-medium ${
              notification.type === 'overdue' 
                ? 'text-red-600' 
                : notification.type === 'due-very-soon'
                  ? 'text-red-500'
                  : 'text-orange-500'
            }`}>
              {notification.timeRemaining}
            </span>
            
            <div className="flex items-center space-x-2">
              {notification.car.rentedUntil && (
                <span className="text-xs text-gray-500">
                  Due: {(() => {
                    // Handle Firestore Timestamp objects
                    let dueDate: Date;
                    if (typeof notification.car.rentedUntil === 'object' && 'seconds' in notification.car.rentedUntil) {
                      dueDate = new Date((notification.car.rentedUntil as any).seconds * 1000);
                    } else {
                      dueDate = new Date(notification.car.rentedUntil);
                    }
                    return dueDate.toLocaleString([], { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                  })()}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
